import BaseBlockComponent from '@js/BaseBlockComponent'

/**
 * Manages appearance and filter updates of the sir/dailygenartquery block
 *
 * @extends BaseBlockComponent
 */
class DailyGenArtQuery extends BaseBlockComponent {
    get grid() {
        const grid = this.element.querySelector('.daily-genart-grid')
        return sir.components[grid.dataset.componentid]
    }

    constructor(element) {
        super(element)

        this.mediaSelector = this.element.querySelector('.media-type-selector')
        this.topicSelector = this.element.querySelector('.topic-selector')
        this.seriesSelector = this.element.querySelector('.series-selector')
        this.wallpapersCheckbox = this.element.querySelector('.wallpapers-checkbox')
        this.commentaryCheckbox = this.element.querySelector('.commentary-checkbox')
        this.draftsCheckbox = this.element.querySelector('.drafts-checkbox')
        this.announcement = this.element.querySelector('.announcement')

        this.updateOptionCounts()

        this.mediaSelector.addEventListener('change', (event) => {
            this.element.dataset.mediaShow = event.target.value
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })

        this.topicSelector?.addEventListener('change', (event) => {
            this.element.dataset.topicShow = event.target.value
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })

        this.seriesSelector?.addEventListener('change', (event) => {
            this.element.dataset.seriesShow = event.target.value
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })

        this.wallpapersCheckbox?.addEventListener('change', (event) => {
            if (event.target.checked) {
                this.element.setAttribute('data-wallpapers-show', '')
            } else {
                this.element.removeAttribute('data-wallpapers-show')
            }
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })

        this.commentaryCheckbox?.addEventListener('change', (event) => {
            if (event.target.checked) {
                this.element.setAttribute('data-commentary-show', '')
            } else {
                this.element.removeAttribute('data-commentary-show')
            }
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })

        this.draftsCheckbox?.addEventListener('change', (event) => {
            if (event.target.checked) {
                this.element.setAttribute('data-drafts-show', '')
            } else {
                this.element.removeAttribute('data-drafts-show')
            }
            this.grid.updateGrid()
            this.updateOptionCounts()
            this.announceFilterChange()
        })
    }

    /**
     * Updates the item count in the filter dropdowns
     */
    updateOptionCounts() {
        let topicSelectorText = ''
        let seriesSelectorText = ''

        const mediaSelectorText = this.mediaSelector.value !== '' ? `[data-media^="${this.mediaSelector.value}"]` : ''
        const wallpapersCheckboxText = this.wallpapersCheckbox.checked ? '[data-wallpapers]' : ''
        const commentaryCheckboxText = this.commentaryCheckbox.checked ? '[data-commentary]' : ''
        const draftsCheckboxText = this.draftsCheckbox?.checked ? '' : ':not([data-draft])'

        if (this.topicSelector) {
            topicSelectorText = this.topicSelector.value !== '' ? `[data-topic~="${this.topicSelector.value}"]` : ''
        }

        if (this.seriesSelector) {
            seriesSelectorText = this.seriesSelector.value !== '' ? `[data-series~="${this.seriesSelector.value}"]` : ''
        }

        const selectors = {
            topic: topicSelectorText,
            series: seriesSelectorText,
            media: mediaSelectorText,
            wallpapers: wallpapersCheckboxText,
            commentary: commentaryCheckboxText,
            drafts: draftsCheckboxText
        }

        const mediaOptions = [...this.mediaSelector.querySelectorAll('option[data-label]')]
        mediaOptions.forEach(option => {
            const itemCount = [...this.element.querySelectorAll(`li[data-media^="${option.value}"]${this.getSelectorMinusOption(selectors, 'media')}`)].length
            option.innerHTML = `${option.dataset.label} (${itemCount})`
            option.disabled = itemCount === 0
        })

        const topicOptions = [...this.topicSelector?.querySelectorAll('option[data-label]') || []]
        topicOptions.forEach(option => {
            const itemCount = [...this.element.querySelectorAll(`li[data-topic~="${option.value}"]${this.getSelectorMinusOption(selectors, 'topic')}`)].length
            option.innerHTML = `${option.dataset.label} (${itemCount})`
            option.disabled = itemCount === 0
        })

        const seriesOptions = [...this.seriesSelector?.querySelectorAll('option[data-label]') || []]
        seriesOptions.forEach(option => {
            const itemCount = [...this.element.querySelectorAll(`li[data-series~="${option.value}"]${this.getSelectorMinusOption(selectors, 'series')}`)].length
            option.innerHTML = `${option.dataset.label} (${itemCount})`
            option.disabled = itemCount === 0
        })

        const wallpaperItemCount = [...this.element.querySelectorAll(`li[data-wallpapers]${this.getSelectorMinusOption(selectors, 'wallpapers')}`)].length
        this.wallpapersCheckbox.disabled = wallpaperItemCount === 0

        const commentaryItemCount = [...this.element.querySelectorAll(`li[data-commentary]${this.getSelectorMinusOption(selectors, 'commentary')}`)].length
        this.commentaryCheckbox.disabled = commentaryItemCount === 0
    }

    /**
     * Triggers an aria-live announcement of the current filter status
     */
    announceFilterChange() {
        const visibleItems = [...this.element.querySelectorAll('li')]
            .filter(item => getComputedStyle(item).display !== 'none')
            .length

        let newText = ''

        const mediaSelectorValue = this.mediaSelector?.value || ''
        const topicSelectorValue = this.topicSelector?.value || ''
        const seriesSelectorValue = this.seriesSelector?.value || ''

        const postText = visibleItems === 1 ? 'post' : 'posts'
        if (!this.topicSelector) {
            if (mediaSelectorValue === '') {
                newText = `Showing all ${visibleItems} ${postText}`
            } else {
                const label = visibleItems === 1 ?
                    this.mediaSelector.selectedOptions[0].dataset.labelSingular :
                    this.mediaSelector.selectedOptions[0].dataset.label
                newText = `Showing ${visibleItems} ${label}`
            }
        } else {
            if (
                mediaSelectorValue === '' &&
                topicSelectorValue === '' &&
                seriesSelectorValue === ''
            ) {
                newText = `Showing all ${visibleItems} ${postText}`
            } else if (
                mediaSelectorValue !== '' &&
                topicSelectorValue === '' &&
                seriesSelectorValue === ''
            ) {
                const label = visibleItems === 1 ?
                    this.mediaSelector.selectedOptions[0].dataset.labelSingular :
                    this.mediaSelector.selectedOptions[0].dataset.label
                newText = `Showing ${visibleItems} ${label}`
            } else if (
                mediaSelectorValue === '' &&
                topicSelectorValue !== '' &&
                seriesSelectorValue === ''
            ) {
                const label = this.topicSelector.selectedOptions[0].dataset.label
                newText = `Showing ${visibleItems} ${label} ${postText}`
            } else if (
                mediaSelectorValue === '' &&
                topicSelectorValue === '' &&
                seriesSelectorValue !== ''
            ) {
                const label = this.seriesSelector.selectedOptions[0].dataset.label
                newText = `Showing ${visibleItems} ${postText} in ${label}`
            } else {
                const mediaLabel = this.mediaSelector?.selectedOptions[0].dataset.labelSingular || ''
                const topicLabel = this.topicSelector?.selectedOptions[0].dataset.label || ''
                const seriesLabel = this.seriesSelector?.selectedOptions[0].dataset.label || ''
                newText = `Showing ${visibleItems} ${topicLabel} ${mediaLabel} ${postText} in ${seriesLabel}`
            }
        }

        if (this.wallpapersCheckbox.checked && this.commentaryCheckbox.checked) {
            newText += ' with wallpapers and commentary'
        } else if (this.wallpapersCheckbox.checked) {
            newText += ' with wallpapers'
        } else if (this.commentaryCheckbox.checked) {
            newText += ' with commentary'
        }

        if (this.draftsCheckbox?.checked) {
            newText += ', including drafts'
        }

        this.announcement.innerHTML = newText
    }

    getSelectorMinusOption(selectors, option) {
        const out = Object.keys(selectors).reduce((accumulator, currentValue) => {
            if (currentValue !== option) {
                return `${accumulator}${selectors[currentValue]}`
            } else {
                return accumulator
            }
        }, '')
        return out
    }
}

DailyGenArtQuery.create('sir/dailygenartquery')
